/**
 * TrialRow — one dense row in the activity stream. Leads with a ~6px per-run
 * color dot (marker only, never a fill on the row surface), then the mono
 * run-id, trial index, scenario, and an operator-vocabulary status token.
 *
 * In `mode='kanban'` the layout inverts: scenario leads (it is the meaningful
 * identifier) followed by a de-emphasized armId. The run-id / trial-index
 * prefix and the per-card status label are suppressed — the column header
 * already carries that context. The ✓/✗ glyph is kept so pass vs fail stays
 * visible inside the Done column.
 *
 * Motion budget: ONE quiet pulse on the status token when the trial is running
 * (list mode only). Done trials are slightly recessed via the lane class.
 * Purely presentational — caller resolves the color from runColor() and routes
 * the click to the drawer.
 */

import { laneForTrial } from './board-lanes'
import type { TrialLite, TrialStatus } from './board-types'
import { trialRowTiming } from './format'

export interface TrialRowProps {
  trial: TrialLite
  /** Stable hex color from runColor(trial.runId) — caller resolves, component is pure. */
  color: string
  /** Called when the row is clicked/activated — parent wires to the drawer hash. */
  onClick: () => void
  /**
   * Rendering context.
   * - `'list'` (default) — full prefix: run-id · t#N, scenario, status token.
   * - `'kanban'` — scenario leads, armId de-emphasized, status label/pulse hidden;
   *   glyph (✓/✗) kept for pass/fail distinction in the Done column.
   */
  mode?: 'list' | 'kanban'
}

/** Map 8 store statuses to operator vocabulary displayed in the status token. */
function statusLabel(status: TrialStatus, attempt: number, maxAttempts: number): string {
  switch (status) {
    case 'queued':
    case 'retryable':
      return 'waiting'
    case 'leased':
      return 'claimed'
    case 'running':
      return 'running'
    case 'interrupted':
      return `recovering ⟳ ${attempt}/${maxAttempts}`
    case 'succeeded':
    case 'failed':
    case 'cancelled':
      return 'done'
  }
}

/** CSS modifier class for the status token. */
function statusClass(status: TrialStatus): string {
  switch (status) {
    case 'queued':
    case 'retryable':
      return 'trs-waiting'
    case 'leased':
      return 'trs-claimed'
    case 'running':
      return 'trs-running'
    case 'interrupted':
      return 'trs-recovering'
    case 'succeeded':
      return 'trs-done-pass'
    case 'failed':
    case 'cancelled':
      return 'trs-done-fail'
  }
}

/** Resolution glyph for terminal statuses: ✓ for success, ✗ for fail/cancel. */
function doneGlyph(status: TrialStatus): string | null {
  if (status === 'succeeded') return '✓'
  if (status === 'failed' || status === 'cancelled') return '✗'
  return null
}

export function TrialRow({ trial, color, onClick, mode = 'list' }: TrialRowProps): React.JSX.Element {
  // A rate-limit `recovering` trial (provider cooling) reads as recovering even
  // though its store status is still queued/retryable, so the token and lane
  // class match the Recovering lane it now renders in (EN-3).
  const recovering = trial.recovering && trial.status !== 'interrupted'
  const label = recovering ? 'rate-limited' : statusLabel(trial.status, trial.attempt, trial.maxAttempts)
  const cls = recovering ? 'trs-recovering' : statusClass(trial.status)
  const glyph = doneGlyph(trial.status)
  const lane = laneForTrial(trial)
  const timing = trialRowTiming(trial)
  const isKanban = mode === 'kanban'

  return (
    <li data-trial-id={trial.id}>
      {/* Button wraps all content — native keyboard support, no tabIndex hack on li */}
      <button type="button" className={`trial-row trial-row-lane-${lane}`} onClick={onClick}>
        {/* ~6px per-run color dot — a marker, never a fill on the row surface */}
        <span
          className="trial-row-dot"
          style={{ backgroundColor: color }}
          aria-hidden="true"
          data-testid="trial-row-dot"
        />
        {/* List mode: run-id · t#N prefix; kanban mode: suppressed — column header supplies status context */}
        {!isKanban && (
          <>
            <span className="trial-row-run-id" title={trial.runId}>
              {trial.runId.slice(0, 8)}
            </span>
            <span className="trial-row-divider" aria-hidden="true">
              ·
            </span>
            <span className="trial-row-trial-id" title={trial.id}>
              t#{trial.trialIndex}
            </span>
          </>
        )}
        <span className="trial-row-scenario" title={trial.scenario}>
          {trial.scenario}
        </span>
        {/* Kanban mode: de-emphasized arm/model identifier after the scenario name */}
        {isKanban && trial.armId && (
          <span className="trial-row-arm-id" title={trial.armId}>
            {trial.armId}
          </span>
        )}
        <span className={`trial-row-status ${cls}`}>
          {/* Pulse: list mode only — kanban column label already signals running state */}
          {!isKanban && trial.status === 'running' && <span className="trial-row-pulse" aria-hidden="true" />}
          {/* Status label: suppressed in kanban mode — column header makes it redundant */}
          {!isKanban && label}
          {/* Glyph always visible: differentiates ✓ pass from ✗ fail inside the Done column */}
          {glyph !== null && <span className="trial-row-glyph">{glyph}</span>}
        </span>
        {/* Human-readable timing: when it ran (absolute clock) + how long it took/has taken. */}
        <span className="trial-row-timing tabular" data-testid="trial-row-timing" title={timing.whenIso}>
          <span className="trial-row-when">{timing.whenClock}</span>
          <span className="trial-row-timing-sep" aria-hidden="true">
            ·
          </span>
          <span className="trial-row-duration">{timing.duration}</span>
        </span>
      </button>
    </li>
  )
}
